import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Settings, Search, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useIntegrationConfigs } from '@/hooks/useIntegrationConfigs';
import { useParserBotSuppliers } from '@/hooks/useParserBotSuppliers';
import { Link, useParams } from 'react-router-dom';

export function ProcessingSourcesPanel() {
  const { workspaceId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  
  const { getIntegrationConfig } = useIntegrationConfigs();
  const { data: parserBotConfig, isLoading: configLoading } = getIntegrationConfig('parser_bot');
  
  const {
    suppliers,
    isLoading: suppliersLoading,
    toggleSupplier,
    enableAll,
    disableAll,
  } = useParserBotSuppliers();

  const isConnected = parserBotConfig?.connection_status === 'connected';
  const isEnabled = parserBotConfig?.is_enabled;

  // Filter suppliers by search
  const filteredSuppliers = suppliers.filter((s) =>
    s.supplier_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const enabledCount = suppliers.filter((s) => s.is_enabled).length;

  if (configLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If Parser Bot not configured
  if (!parserBotConfig || !isConnected) {
    return (
      <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
        <Settings className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertDescription>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-100">Parser Bot Integration Setup Required</p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Complete these steps to enable automatic vendor parsing:
              </p>
            </div>
            
            <ol className="text-sm text-amber-800 dark:text-amber-200 space-y-2 ml-4 list-decimal">
              <li>Navigate to Parser Integration Settings</li>
              <li>Enter your BillPortal API Base URL</li>
              <li>Click <strong>"Save & Sync Suppliers"</strong></li>
              <li>Return here to manage vendor toggles</li>
            </ol>
            
            <Link to={`/w/${workspaceId}/dashboard/tum/parser-integration`}>
              <Button size="sm" className="mt-2">
                <Settings className="h-4 w-4 mr-2" />
                Set Up Parser Integration
              </Button>
            </Link>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Parser Bot Active
                </Badge>
              </div>
              <CardTitle className="text-base text-green-900 dark:text-green-100">
                Connected to BillPortal.io
              </CardTitle>
              <CardDescription className="mt-1 text-green-700 dark:text-green-300">
                {suppliers.length} vendors available • {enabledCount} enabled for auto-parsing
              </CardDescription>
            </div>
            <Link to={`/w/${workspaceId}/dashboard/tum/parser-integration`}>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      {/* Vendor Auto-Parsing */}
      <Card>
        <CardHeader>
          <CardTitle>Vendor Auto-Parsing</CardTitle>
          <CardDescription>
            Toggle vendors for automatic bill parsing. Bills from enabled vendors will be parsed automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Bulk Actions */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => enableAll.mutate()}
              disabled={enableAll.isPending}
            >
              Enable All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => disableAll.mutate()}
              disabled={disableAll.isPending}
            >
              Disable All
            </Button>
          </div>

          {/* Suppliers Grid */}
          {suppliersLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery ? 'No vendors found matching your search' : 'No vendors available'}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredSuppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{supplier.supplier_name}</span>
                      {supplier.is_enabled ? (
                        <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* Stats */}
                    {supplier.total_parsed > 0 ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {supplier.success_count}/{supplier.total_parsed} success
                        </span>
                        {supplier.avg_confidence && (
                          <Badge variant="outline" className="text-xs">
                            {Math.round(supplier.avg_confidence * 100)}% conf
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">No data yet</span>
                    )}
                  </div>
                  
                  <Switch
                    checked={supplier.is_enabled}
                    onCheckedChange={(enabled) =>
                      toggleSupplier.mutate({ supplierId: supplier.id, enabled })
                    }
                    disabled={toggleSupplier.isPending}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">How Auto-Parsing Works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            • Bills from <strong>enabled vendors</strong> are automatically parsed via BillPortal.io API
          </p>
          <p>
            • Successfully parsed bills skip manual review and move directly to <strong>Validation</strong>
          </p>
          <p>
            • Toggle vendors off to force manual review for specific utilities
          </p>
          <p>
            • All vendors are <strong>enabled by default</strong> after syncing
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
